import styled from "@emotion/styled";
import { FC, useMemo } from "react";
import Translate from "components/Translate/Translate";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { BehavioralHealthDialogCrisis } from "./BehavioralHealthDialogCrisis";
import { BehavioralAssessment } from "./BehavioralAssessment";
import { useBlocNext } from "@blac/react";
import { BehavioralAssessmentDialogStructure } from "./BehavioralAssessmentDialogStructure";
import { BehavioralAssessmentActionSwitch } from "./BehavioralAssessmentActionSwitch";

const CenterEl = styled.div`
  border-radius: 0.6em;
  height: 100%;
  text-align: center;
  margin: 0 auto;
  display: flex;
  justify-content: center;

  @media (orientation: portrait) {
    text-align: center;
  }
`;

const BehavioralAssessmentWrapper = styled.div`
  display: flex;
  height: 100%;
  flex-flow: column nowrap;
  flex: 0 0 474px;
  font-family: var(--font-family);

  @media screen and (max-width: 730px) {
    flex: 0 0 328px;
  }
`;

const BehavioralAssessmentTitle = styled.h4`
  flex: 1 0 auto;
  align-content: end;
  font-feature-settings:
    "liga" off,
    "clig" off;

  font-size: 2.5rem;
  font-style: normal;
  font-weight: 300;
  line-height: 110%; /* 44px */
  letter-spacing: -0.063rem;

  margin-bottom: 24px;
`;

const BehavioralAssessmentDescription = styled.div`
  flex: 0 0 auto;
  font-feature-settings:
    "liga" off,
    "clig" off;

  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 22.4px */
  letter-spacing: -0.02rem;
  margin-bottom: 40px;
`;

const BehavioralAssessmentAction = styled.div`
  display: flex;
  flex: 1 0 auto;
  align-items: start;
  justify-content: center;
  font-feature-settings:
    "liga" off,
    "clig" off;

  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 22.4px */
  letter-spacing: -0.02rem;
  margin-bottom: 40px;
`;

interface HBehavioralHealthDialogAssessmentProps {
  assessment: BehavioralAssessment;
}

/**
 * Assessment dialog page of behavioral questionnaire (GAD-7, PHQ-8)
 *
 * General structure: Title, Message, ActionButton, Crisis hotline(optional)
 * https://www.figma.com/design/kUt51hgo6pKLtwoKEkih6M/%E2%9C%94-App?node-id=13044-21417&m=dev
 *
 * @param assessment
 * @constructor
 */
export const BehavioralHealthDialogAssessment: FC<
  HBehavioralHealthDialogAssessmentProps
> = ({ assessment }) => {
  const [, { firstName }] = useBlocNext(UserPreferencesCubit);

  const structure = useMemo<BehavioralAssessmentDialogStructure>(() => {
    switch (assessment) {
      case "allRight":
        return {
          title: "behavioralHealthDialog_title_allRight",
          description: "behavioralHealthDialog_description_allRight",
          action: "behavioralHealthDialog_action_complete",
          crisisRequired: false
        };
      case "maybe":
        return {
          title: "behavioralHealthDialog_title_maybe",
          description: "behavioralHealthDialog_description_maybe",
          action: "behavioralHealthDialog_action_connectWithLyra",
          crisisRequired: true
        };
      case "recommend":
        return {
          title: "behavioralHealthDialog_title_recommend",
          description: "behavioralHealthDialog_description_recommend",
          action: "behavioralHealthDialog_action_connectWithLyra",
          crisisRequired: true
        };
      default:
        throw new Error("Unknown behavioral health dialog assessment.");
    }
  }, [assessment]);

  return (
    <CenterEl>
      <BehavioralAssessmentWrapper>
        <BehavioralAssessmentTitle>
          <Translate msg={structure.title} variables={{ firstName }} />
        </BehavioralAssessmentTitle>
        <BehavioralAssessmentDescription>
          <Translate msg={structure.description} />
        </BehavioralAssessmentDescription>
        <BehavioralAssessmentAction>
          <BehavioralAssessmentActionSwitch
            assessment={assessment}
            structure={structure}
          />
        </BehavioralAssessmentAction>
        {structure.crisisRequired && <BehavioralHealthDialogCrisis />}
      </BehavioralAssessmentWrapper>
    </CenterEl>
  );
};
